// File: src/pages/AIReports.js
// AI Reports: research reports written with AI research assistants. The manufacturing and
// everyday-life reports are static HTML bundles in public/ai-reports/<section>/<slug>/ (built by
// scripts/build_ai_reports.py and served by client_app.py); the rest are React pages.
import React, { useContext, useEffect } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { MobileViewContext } from "../context/MobileViewContext";
import SecondarySidebar from "../components/SecondarySidebar";
import NumericalDataStores from "./NumericalDataStores";
import AgentMemoryMap from "./AgentMemoryMap";
import StateOfAiInLogistics from "./StateOfAiInLogistics";
import DeeperNotFaster from "./DeeperNotFaster";
import "./AIReports.css";

// `external: true` marks a static bundle: it needs a full page load, not a router change.
export const aiReportsData = [
  {
    title: "Manufacturing",
    items: [
      {
        label: "AI in manufacturing: a value-chain map of 142 startups",
        route: "/ai-reports/manufacturing/ai-value-chain-map/",
        external: true,
        description:
          "Where AI startups sit in a factory, from the sensor on the machine to the planner's desk, and the gap between companies that almost none of them reach.",
      },
      {
        label: "Why factories adopt machine learning faster than optimisation",
        route: "/ai-reports/manufacturing/ml-vs-or-adoption/",
        external: true,
        description:
          "Prediction tools spread across industry while planning and scheduling software stayed niche. What made the difference, and what optimisation could copy.",
      },
      {
        label: "Operations research in the field: LLMs, adoption and robots",
        route: "/ai-reports/manufacturing/or-in-the-field/",
        external: true,
        description:
          "Three reports in one: what language models really add to optimisation, why optimisation projects stall in practice across eight language regions, and what changes when robots carry out the plan.",
      },
    ],
  },
  {
    title: "Everyday life",
    items: [
      {
        label: "Meat, eggs and dairy in Germany: which label, which shop, what life",
        route: "/ai-reports/everyday/meat-egg-dairy-guide-germany/",
        external: true,
        description:
          "What the animal welfare labels on German meat, eggs, milk and cheese actually mean, what each supermarket's own brands promise, and where to shop in Frankfurt-Bornheim.",
      },
      {
        label: "Estonia vs Germany (Hessen): how much time bureaucracy costs",
        route: "/ai-reports/everyday/estonia-vs-germany-bureaucracy/",
        external: true,
        description:
          "Ninety-six everyday dealings with the state, from tax returns to registering a move, compared side by side, with measured online use, waiting times and fees.",
      },
    ],
  },
  {
    title: "Logistics",
    items: [
      {
        label: "Fleet Routing Without Memory (Sep 2026)",
        route: "/ai-reports/state-of-ai-in-logistics",
        description:
          "The state of AI for planning delivery routes and running vehicle fleets, to September 2026: learned solvers, routing from a fleet's own history, travel-time estimates, the data and the market.",
      },
    ],
  },
  {
    title: "Agents",
    items: [
      {
        label: "Current State of Agent Memory (Sep 2026)",
        route: "/ai-reports/current-state-of-agent-memory-sep-2026",
        description:
          "Why AI assistants forget, the fixes people are trying, and the side effects each fix brings.",
      },
      {
        label: "Niche Agent Industries: Deeper, Not Faster",
        route: "/ai-reports/deeper-not-faster-agents",
        description:
          "Forty-six niche markets for AI agents, sorted by whether anything real can tell a good agent from a bad one.",
      },
    ],
  },
  {
    title: "Data",
    items: [
      {
        label: "Numerical Data Stores",
        route: "/ai-reports/numerical-data-stores",
        description:
          "An interactive comparison of the ways to store large tables of numbers, from plain arrays to time-series databases.",
      },
    ],
  },
];

function ReportLink({ item }) {
  return item.external ? (
    <a href={item.route}>{item.label}</a>
  ) : (
    <Link to={item.route}>{item.label}</Link>
  );
}

function AIReportsHome() {
  return (
    <div className="air-home">
      <Helmet>
        <title>AI Reports | Helia Jamshidi</title>
        <meta
          name="description"
          content="Research reports written with AI research assistants: AI in manufacturing, logistics, agents and data, plus practical guides to everyday life in Germany."
        />
        <meta name="author" content="Helia Jamshidi" />
        <meta property="og:title" content="AI Reports | Helia Jamshidi" />
        <meta property="og:url" content="https://heliajamshidi.me/ai-reports" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://heliajamshidi.me/ai-reports" />
      </Helmet>
      <h1>AI Reports</h1>
      <p className="air-intro">
        Research reports written with AI research assistants: where AI is used in industry and where it
        stalls, plus a few practical guides to everyday life in Germany. Manufacturing first, then
        everyday life, logistics, agents and data.
      </p>
      {aiReportsData.map((group) => (
        <section key={group.title} className="air-group">
          <h2>{group.title}</h2>
          <ul className="air-list">
            {group.items.map((item) => (
              <li key={item.route}>
                <ReportLink item={item} />
                <p>{item.description}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// In-app navigation to a static bundle (for example a stale link) gets a full page load.
function StaticReport({ section }) {
  const { slug } = useParams();
  useEffect(() => {
    window.location.replace(`/ai-reports/${section}/${slug}/`);
  }, [section, slug]);
  return <p>Loading report...</p>;
}

export default function AIReports({ isMobile }) {
  const { contentIsVisibleMobile, toc2IsVisibleMobile } = useContext(MobileViewContext);

  return (
    <div className="content-with-sub">
      {(!isMobile || toc2IsVisibleMobile) && (
        <SecondarySidebar heading="AI Reports" categories={aiReportsData} isMobile={isMobile} />
      )}

      {(!isMobile || contentIsVisibleMobile) && (
        <div className="content-area">
          <Routes>
            <Route path="/" element={<AIReportsHome />} />
            <Route path="/manufacturing/:slug/*" element={<StaticReport section="manufacturing" />} />
            <Route path="/everyday/:slug/*" element={<StaticReport section="everyday" />} />
            <Route path="/numerical-data-stores" element={<NumericalDataStores />} />
            <Route path="/current-state-of-agent-memory-sep-2026" element={<AgentMemoryMap />} />
            <Route path="/state-of-ai-in-logistics" element={<StateOfAiInLogistics />} />
            <Route path="/deeper-not-faster-agents" element={<DeeperNotFaster />} />
          </Routes>
        </div>
      )}
    </div>
  );
}
